import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FiCode, FiFolder, FiPlus, FiSliders, FiUpload, FiFileText, FiDownload, FiEdit2, FiTrash2, FiCopy } from 'react-icons/fi';
import { toast } from '../../Toast';
import Toggle from '../../Toggle';
import { copyText } from '../../clientSettings/widgets/shared';
import RemoveItemModal from '../RemoveItemModal';
import { BackLink, HubHeader, SearchBox, IconButton, hubPath, useHubSite } from '../shared';
import { DOCS, Doc, FOLDERS, Folder, FolderSort, folderSlug, shortcode, today } from './documents/docsData';
import NewFolderModal from './documents/NewFolderModal';
import FolderSettingsModal from './documents/FolderSettingsModal';
import DocumentModal, { DocDraft } from './documents/DocumentModal';

// day-month-year dates ("21 Aug 2026") → sortable number
const dateValue = (d: string) => new Date(d).getTime() || 0;

// six-dot drag handle (Feather has no grip icon)
const Grip: React.FC = () =>
    <svg className="hub-grip" width="1em" height="1em" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        {[6, 12, 18].map(y => <React.Fragment key={y}><circle cx="9" cy={y} r="1.6" /><circle cx="15" cy={y} r="1.6" /></React.Fragment>)}
    </svg>;

type Removing = { kind: 'doc'; doc: Doc } | { kind: 'folder'; folder: Folder };

// Website Hub › Website content › Other documents (spec 406:9505)
const OtherDocuments: React.FC = () => {
    const history = useHistory();
    const { host } = useHubSite();
    const [folders, setFolders] = useState(FOLDERS);
    const [docs, setDocs] = useState(DOCS);
    const [folderId, setFolderId] = useState(FOLDERS[0].id);
    const [q, setQ] = useState('');
    const [newFolder, setNewFolder] = useState(false);
    const [folderSettings, setFolderSettings] = useState(false);
    // 'add' = Upload to This Folder, a Doc = Edit
    const [editing, setEditing] = useState<Doc | 'add' | null>(null);
    const [removing, setRemoving] = useState<Removing | null>(null);
    const [dragId, setDragId] = useState<string | null>(null);

    const folder = folders.find(f => f.id === folderId) ?? folders[0];
    const inFolder = (id: string) => docs.filter(d => d.folderId === id);
    // newest first sorts by upload date; "the order I choose" keeps the list order (drag to change it)
    const ordered = folder.sort === 'newest'
        ? [...inFolder(folder.id)].sort((a, b) => dateValue(b.uploaded) - dateValue(a.uploaded))
        : inFolder(folder.id);
    const shown = ordered.filter(d => d.name.toLowerCase().includes(q.trim().toLowerCase()));
    const code = shortcode(folder);

    const openFolder = (id: string) => { setFolderId(id); setQ(''); };

    const createFolder = (name: string, description: string, sort: FolderSort) => {
        const f: Folder = { id: `f${Date.now()}`, name, description, path: folderSlug(name), sort };
        setFolders(xs => [...xs, f]);
        openFolder(f.id);
        setNewFolder(false);
        toast.info('Preview only: the folder was added here, nothing was saved');
    };

    const saveFolder = (f: Folder) => {
        setFolders(xs => xs.map(x => x.id === f.id ? f : x));
        setFolderSettings(false);
        toast.info('Preview only: the folder settings changed here, nothing was saved');
    };

    const toggleLive = (d: Doc, live: boolean) => {
        setDocs(xs => xs.map(x => x.id === d.id ? { ...x, live } : x));
        toast.info(`Preview only: ${live ? 'shown' : 'hidden'} here, the website wasn’t changed`);
    };

    const saveDoc = (draft: DocDraft) => {
        if (editing === 'add') {
            const file = draft.file ?? { name: '', kind: 'PDF', size: '' };
            const d: Doc = { id: `d${Date.now()}`, folderId: draft.folderId, name: draft.name, file: file.name, kind: file.kind, size: file.size, uploaded: today(), uploadedBy: 'You', live: draft.live, description: draft.description, versions: [{ n: 1, date: today() }] };
            // a new file goes to the top of a hand-ordered folder
            setDocs(xs => [d, ...xs]);
            toast.info('Preview only: the document was added here, nothing was uploaded');
        }
        else if (editing) {
            const prev = editing;
            setDocs(xs => xs.map(x => {
                if (x.id !== prev.id) return x;
                const next = { ...x, name: draft.name, folderId: draft.folderId, description: draft.description, live: draft.live };
                return draft.file
                    ? { ...next, file: draft.file.name, kind: draft.file.kind, size: draft.file.size, uploaded: today(), uploadedBy: 'You', versions: [{ n: x.versions[0].n + 1, date: today() }, ...x.versions] }
                    : next;
            }));
            const moved = folders.find(f => f.id === draft.folderId);
            toast.info(draft.folderId !== prev.folderId && moved
                ? `Preview only: moved to ${moved.name} here, the website wasn’t changed`
                : 'Preview only: the changes were made here, the website wasn’t changed');
        }
        setEditing(null);
    };

    const remove = () => {
        if (!removing) return;
        if (removing.kind === 'doc') {
            setDocs(xs => xs.filter(x => x.id !== removing.doc.id));
        }
        else {
            const id = removing.folder.id;
            const rest = folders.filter(f => f.id !== id);
            setFolders(rest);
            setDocs(xs => xs.filter(x => x.folderId !== id));
            if (rest.length) openFolder(rest[0].id);
        }
        setRemoving(null);
        toast.info('Preview only: removed here, the website wasn’t changed');
    };

    // drag a row onto another to move it there; switches the folder to "the order I choose"
    const drop = (targetId: string) => {
        if (!dragId || dragId === targetId) return;
        setDocs(xs => {
            // start from the order on screen so a newest-first folder keeps its look until the move
            const ids = ordered.map(d => d.id);
            const from = ids.indexOf(dragId);
            const to = ids.indexOf(targetId);
            ids.splice(to, 0, ...ids.splice(from, 1));
            const mine = ids.map(id => xs.find(x => x.id === id)).filter((x): x is Doc => !!x);
            return [...mine, ...xs.filter(x => x.folderId !== folder.id)];
        });
        if (folder.sort !== 'manual') {
            setFolders(xs => xs.map(x => x.id === folder.id ? { ...x, sort: 'manual' } : x));
            toast.info('This folder now shows files in the order you choose');
        }
        setDragId(null);
    };

    // the page always shows one folder, so the last one can't be deleted
    const canDeleteFolder = folders.length > 1;

    return <>
        <BackLink to={hubPath('content')} label="Back to Website content" />
        <HubHeader title="Other Documents" sub="Any document library — ESG reports, community newsletters, approvals — shown wherever you place the Documents widget">
            <button type="button" className="btn-outline with-icon" onClick={() => history.push('/client-settings/widgets')}><FiCode /> Widget Settings</button>
        </HubHeader>

        <div className="hub-docs">
            <aside className="card hub-folders">
                <span className="hub-folders-h">Folders</span>
                {folders.map(f => {
                    const n = inFolder(f.id).length;
                    return <button key={f.id} type="button" className={`hub-folder${f.id === folder.id ? ' active' : ''}`} onClick={() => openFolder(f.id)}>
                        <span className="hub-folder-ic"><FiFolder /></span>
                        <span className="hub-folder-t"><strong>{f.name}</strong><span>{n} {n === 1 ? 'file' : 'files'} · {n ? 'on website' : 'empty'}</span></span>
                    </button>;
                })}
                <button type="button" className="link hub-new-folder" onClick={() => setNewFolder(true)}><FiPlus /> New Folder</button>
            </aside>

            <section className="card hub-folder-card">
                <div className="section-title">
                    <span className="icon-tile"><FiFolder /></span>
                    <div className="text">
                        <h2>{folder.name}</h2>
                        <div className="hint">{ordered.length} {ordered.length === 1 ? 'file' : 'files'} · shown on {host}/{folder.path}/ via the Documents widget</div>
                    </div>
                </div>

                <div className="hub-toolbar">
                    <SearchBox value={q} onChange={setQ} placeholder="Search in this folder" />
                    <span className="hub-spacer" />
                    <button type="button" className="btn-outline with-icon" onClick={() => setFolderSettings(true)}><FiSliders /> Folder Settings</button>
                    <button type="button" className="btn-primary with-icon" onClick={() => setEditing('add')}><FiUpload /> Upload to This Folder</button>
                </div>

                <div className="hub-table hub-doc-table">
                    <div className="tr th"><span className="item">File</span><span className="date">Uploaded</span><span className="hub-live">On website</span><span className="act" /></div>
                    {shown.map(d => <div key={d.id} className={`tr${dragId === d.id ? ' hub-dragging' : ''}`} draggable
                        onDragStart={e => { setDragId(d.id); e.dataTransfer.effectAllowed = 'move'; }}
                        onDragOver={e => { if (dragId) e.preventDefault(); }}
                        onDrop={e => { e.preventDefault(); drop(d.id); }}
                        onDragEnd={() => setDragId(null)}>
                        <span className="item">
                            <span className="hub-drag" title="Drag to reorder"><Grip /></span>
                            <span className="hub-doc-ic"><FiFileText /></span>
                            <span className="t"><strong>{d.name}</strong><span>{d.kind} · {d.size}</span></span>
                        </span>
                        <span className="date">{d.uploaded}</span>
                        <span className="hub-live"><Toggle checked={d.live} onChange={v => toggleLive(d, v)} label={`Show ${d.name} on the website`} /></span>
                        <span className="act">
                            <IconButton label="Download" onClick={() => toast.info('Preview only: the file isn’t stored yet')}><FiDownload /></IconButton>
                            <IconButton label="Edit" onClick={() => setEditing(d)}><FiEdit2 /></IconButton>
                            <IconButton label="Remove" danger onClick={() => setRemoving({ kind: 'doc', doc: d })}><FiTrash2 /></IconButton>
                        </span>
                    </div>)}
                    {shown.length === 0 && <p className="hub-empty">{ordered.length ? 'No file matches — try another search.' : 'No files in this folder yet — upload the first one.'}</p>}
                </div>

                <div className="hub-where">
                    <strong>Where This Shows</strong>
                    <p>Paste this shortcode into any WordPress page. Files appear in the order above.</p>
                    <div className="hub-shortcode">
                        <code>{code}</code>
                        <button type="button" onClick={() => copyText(code, 'shortcode')}><FiCopy /> Copy</button>
                    </div>
                </div>
            </section>
        </div>

        {newFolder && <NewFolderModal onClose={() => setNewFolder(false)} onCreate={createFolder} />}
        {folderSettings && <FolderSettingsModal folder={folder} files={ordered.length} onClose={() => setFolderSettings(false)} onSave={saveFolder}
            onDelete={() => {
                if (!canDeleteFolder) {
                    toast.error('Keep at least one folder — add another before deleting this one');
                    return;
                }
                setFolderSettings(false);
                setRemoving({ kind: 'folder', folder });
            }} />}
        {editing && <DocumentModal folders={folders} folderId={folder.id} doc={editing === 'add' ? undefined : editing}
            onClose={() => setEditing(null)} onSave={saveDoc}
            onDelete={editing === 'add' ? undefined : () => { setEditing(null); setRemoving({ kind: 'doc', doc: editing }); }} />}
        {removing && <RemoveItemModal noun={removing.kind === 'doc' ? 'Document' : 'Folder'} name={removing.kind === 'doc' ? removing.doc.name : removing.folder.name}
            onClose={() => setRemoving(null)} onRemove={remove}
            onHide={removing.kind === 'doc' ? () => {
                const id = removing.doc.id;
                setDocs(xs => xs.map(x => x.id === id ? { ...x, live: false } : x));
                setRemoving(null);
                toast.info('Preview only: hidden here, the website wasn’t changed');
            } : undefined} />}
    </>;
};

export default OtherDocuments;
